import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { trackSearchWithinResults } from '../../utils/tracking/searchBrowse/filterTracking.ts';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants.ts';
import { SbSharedRefineMenuFilters } from '../SbSharedRefineMenu/SbSharedRefineMenuFilters/SbSharedRefineMenuFilters.tsx';
import { SbSharedRefineMenuDisplayPrefsSelect } from '../SbSharedRefineMenu/SbSharedRefineMenuDisplayPrefsSelect/SbSharedRefineMenuDisplayPrefsSelect.jsx';
import { SbSharedRefineMenuSearchWithin } from '../SbSharedRefineMenu/SbSharedRefineMenuSearchWithin/SbSharedRefineMenuSearchWithin.jsx';
import { sbRespRefineMenuFiltersMap } from './SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

import { PRICE } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';
import { CATEGORY_LEVEL_1 } from '../../utils/categoryHelpers.ts';
import { ATTRIBUTE_ITEMTYPE, ATTRIBUTE_JEWELRYTYPE } from '../../constants/attributesConstants.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { uriIncludesVertical } from '../../utils/uriUtils.ts';

import { type SbRespRefineMenu_viewer$key } from './__generated__/SbRespRefineMenu_viewer.graphql.ts';
import { type SbRespRefineMenu_itemSearch$key } from './__generated__/SbRespRefineMenu_itemSearch.graphql.ts';
import { type SbRespRefineMenu_filters$key } from './__generated__/SbRespRefineMenu_filters.graphql.ts';
import { type SbRespRefineMenu_user$key } from './__generated__/SbRespRefineMenu_user.graphql.ts';

const viewerFragment = graphql`
    fragment SbRespRefineMenu_viewer on Viewer {
        ...SbSharedRefineMenuFilters_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespRefineMenu_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        pageType
        ...SbSharedRefineMenuFilters_itemSearch
        ...SbSharedRefineMenuDisplayPrefsSelect_itemSearch
        ...SbSharedRefineMenuSearchWithin_itemSearch
    }
`;
const filtersFragment = graphql`
    fragment SbRespRefineMenu_filters on SearchBrowseFilter @relay(plural: true) {
        ...SbSharedRefineMenuFilters_filters
        ...SbSharedRefineMenuDisplayPrefsSelect_filters
    }
`;

const userFragment = graphql`
    fragment SbRespRefineMenu_user on User {
        ...SbSharedRefineMenuFilters_user
        ...SbSharedRefineMenuDisplayPrefsSelect_user
    }
`;

type Props = {
    viewer: SbRespRefineMenu_viewer$key;
    itemSearch: SbRespRefineMenu_itemSearch$key;
    filters: SbRespRefineMenu_filters$key;
    user: SbRespRefineMenu_user$key | null | undefined;
    isClient: boolean;
    uriRef: string;
};

export const SbRespRefineMenu: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    filters: filtersRef,
    user: userRef,
    isClient,
    uriRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filters = useFragment(filtersFragment, filtersRef);
    const user = useFragment(userFragment, userRef);
    const { isRemoveCountsVariant } = useServerVarsContext();

    const { displayUriRef, pageType } = itemSearch;

    const initiallyExpandedFilters = uriIncludesVertical(displayUriRef || '')
        ? [CATEGORY_LEVEL_1, PRICE]
        : [CATEGORY_LEVEL_1, PRICE, ATTRIBUTE_ITEMTYPE, ATTRIBUTE_JEWELRYTYPE];

    return (
        <>
            <SbSharedRefineMenuSearchWithin
                horizontalSpacing="small"
                onSubmit={trackSearchWithinResults}
                itemSearch={itemSearch}
                hideResultsCount={isRemoveCountsVariant}
            />
            <SbSharedRefineMenuFilters
                user={user}
                uriRef={uriRef}
                viewer={viewer}
                filters={filters}
                isClient={isClient}
                itemSearch={itemSearch}
                filterMap={sbRespRefineMenuFiltersMap}
                initiallyExpanded={filterName => initiallyExpandedFilters.includes(filterName)}
            />
            {pageType !== pageTypes.DESIGN && (
                <SbSharedRefineMenuDisplayPrefsSelect
                    user={user}
                    filters={filters}
                    itemSearch={itemSearch}
                    isClient={isClient}
                />
            )}
        </>
    );
};
